import { backendTopics } from "./const/backendTopics.ts";

let _deps = null;
const DEPS_NOT_SET =
  "Import broker dependencies not set. Call setBrokerDependencies(deps) from the app.";

export function setBrokerDependencies(deps) {
  _deps = deps;
}

function getDeps() {
  if (!_deps) {
    throw new Error(DEPS_NOT_SET);
  }
  return _deps;
}

async function publishApiProxy(command, args) {
  const deps = getDeps();
  const cmd = command.command;
  let result;
  if (cmd === "getCurrentDateTime") {
    result = new Date();
    return result;
  }
  if (cmd === "createExportTag") {
    const parameter = args.parameter;
    const body = {
      name: parameter.name,
      type: parameter.type,
      metadata: parameter.metadata,
    };
    result = await deps.automationApiV1Request("POST", "tags", body);
    return result;
  }
  if (cmd === "exportTaggedContacts") {
    const parameter = args.parameter;
    result = await deps.automationApiV1Request(
      "POST",
      `tags/${parameter.tagId}/contacts`,
      parameter.contacts
    );
    return result;
  }
  if (cmd === "addMetadataToTag") {
    const parameter = args.parameter;
    result = await deps.automationApiV1Request(
      "POST",
      `tags/${parameter.tagId}/metadata`,
      parameter.metadata
    );
    return result;
  }
  if (cmd === "getLinkedInContactsFromSearchPage") {
    const parameter = args.parameter;
    const parameters = [];
    if (parameter.searchRequest && parameter.searchRequest.parameters) {
      for (const key in parameter.searchRequest.parameters) {
        parameters.push({
          key,
          value: parameter.searchRequest.parameters[key],
        });
      }
    }
    const payload = {
      searchRequest: {
        start: parameter.searchRequest?.start ?? 0,
        pageSize: parameter.searchRequest?.pageSize ?? 10,
        parameters,
      },
      referrer: parameter.referrer,
    };
    result = await deps.automationApiV2Request(
      "POST",
      "linkedin-contacts/get-from-search-page",
      payload
    );
    return result;
  }
  if (cmd === "getLinkedInContactsFromGroupPage") {
    const parameter = args.parameter;
    result = await deps.automationApiV2Request(
      "POST",
      "linkedin-contacts/get-from-group-page",
      parameter
    );
    return result;
  }
  if (cmd === "getSalesNavigatorContactsFromSearchPage") {
    const parameter = args.parameter;
    result = await deps.automationApiV2Request(
      "POST",
      "sales-navigator-contacts/get-from-search-page",
      parameter
    );
    return result;
  }
  if (cmd === "getSalesNavigatorContactsFromLeadsList") {
    const parameter = args.parameter;
    result = await deps.automationApiV2Request(
      "POST",
      "sales-navigator-contacts/get-from-leads-list",
      parameter
    );
    return result;
  }
  if (cmd === "getLinkedInConnectionsCount") {
    result = await deps.automationApiV2Request("GET", "connections/count");
    return result;
  }
  if (cmd === "getLinkedInContactsFromMyConnections") {
    const parameter = args.parameter;
    result = await deps.automationApiV2Request(
      "POST",
      "linkedin-contacts/get-from-my-connections-page",
      parameter
    );
    return result;
  }
  throw new Error(`Unknown apiProxy command: ${cmd}`);
}

const IMPORT_ID_PLACEHOLDER = "{{import_id}}";

function buildImportedProfilesUrlTemplate(baseUrl) {
  const baseClean = (baseUrl || "").replace(/\/$/, "");
  const result = `${baseClean}/contacts/import/${IMPORT_ID_PLACEHOLDER}`;
  return result;
}

async function publishExportContacts(command, args) {
  const cmd = command.command;
  if (cmd === "getExportHref") {
    const deps = getDeps();
    const exportId = args.parameter.exportId;
    const template = buildImportedProfilesUrlTemplate(deps.getAppBaseUrl());
    const regExp = new RegExp(
      IMPORT_ID_PLACEHOLDER.replace(/[{}]/g, "\\$&"),
      "g"
    );
    const result = template.replace(regExp, exportId);
    return result;
  }
  throw new Error(`Unknown exportContacts command: ${cmd}`);
}

async function publishReliability(command, args) {
  const cmd = command.command;
  if (cmd === "reliablePublish") {
    const innerCommand = args.parameter.command;
    const result = await broker.publish(innerCommand);
    return result;
  }
  throw new Error(`Unknown reliability command: ${cmd}`);
}

async function publishLinkedInProxyHeaders(command, args) {
  const deps = getDeps();
  const cmd = command.command;
  let result;
  if (cmd === "getLastLinkedInRequestHeaders") {
    if (deps.getLinkedInHeadersFromExtension) {
      result = await deps.getLinkedInHeadersFromExtension(args.metadata);
      if (result == null) {
        result = {};
      }
      return result;
    }
    result = {};
    return result;
  }
  if (cmd === "saveLinkedInHeaders") {
    if (deps.saveLinkedInHeadersInExtension) {
      await deps.saveLinkedInHeadersInExtension(args.metadata);
    }
    return result;
  }
  throw new Error(`Unknown linkedInProxyHeaders command: ${cmd}`);
}

export const broker = {
  async publish(command) {
    const target = command.target;
    const args = command.args || {};
    let result;
    if (target === backendTopics.apiProxy) {
      result = await publishApiProxy(command, args);
      return result;
    }
    if (target === backendTopics.exportContacts) {
      result = await publishExportContacts(command, args);
      return result;
    }
    if (target === backendTopics.reliability) {
      result = await publishReliability(command, args);
      return result;
    }
    if (target === backendTopics.linkedInProxyHeaders) {
      result = await publishLinkedInProxyHeaders(command, args);
      return result;
    }
    if (target === backendTopics.localStorage) {
      const cmd = command.command;
      if (cmd === "getSnailyProfile") {
        result = await getDeps().getSnailyProfile(args.metadata);
        return result;
      }
      throw new Error(`Unknown localStorage command: ${cmd}`);
    }
    if (target === backendTopics.logging) {
      return result;
    }
    if (
      target === backendTopics.linkedInService ||
      target === backendTopics.salesNavigatorService ||
      target === backendTopics.linkedInProxyV2
    ) {
      const deps = getDeps();
      if (deps.sendCommandToExtension) {
        result = await deps.sendCommandToExtension(command);
        return result;
      }
      throw new Error(
        "Only backend automation is supported. Use backend (useBackend=true)."
      );
    }
    throw new Error(`Unknown command target: ${target}`);
  },

  async fireEvent(event) {
    const deps = getDeps();
    if (deps.onExportEvent) {
      deps.onExportEvent(event);
    }
  },
};

export const backendCommandsFactory = {
  apiProxy: {
    createGetCurrentDateTime(metadata) {
      const result = {
        target: backendTopics.apiProxy,
        command: "getCurrentDateTime",
        args: {
          metadata,
        },
      };
      return result;
    },
    createCreateExportTag(name, type, exportMetadata, metadata) {
      const result = {
        target: backendTopics.apiProxy,
        command: "createExportTag",
        args: {
          parameter: {
            name,
            type,
            metadata: exportMetadata,
          },
          metadata,
        },
      };
      return result;
    },
    createExportTaggedContacts(tagId, contacts, metadata) {
      const result = {
        target: backendTopics.apiProxy,
        command: "exportTaggedContacts",
        args: {
          parameter: {
            tagId,
            contacts,
          },
          metadata,
        },
      };
      return result;
    },
    createAddMetadataToTag(tagId, exportMetadata, metadata) {
      const result = {
        target: backendTopics.apiProxy,
        command: "addMetadataToTag",
        args: {
          parameter: {
            tagId,
            metadata: exportMetadata,
          },
          metadata,
        },
      };
      return result;
    },
    backendAutomation: {
      createGetLinkedInContactsFromSearchPage(
        searchRequest,
        referrer,
        metadata
      ) {
        const result = {
          target: backendTopics.apiProxy,
          command: "getLinkedInContactsFromSearchPage",
          args: {
            parameter: {
              searchRequest,
              referrer,
            },
            metadata,
          },
        };
        return result;
      },
      createGetLinkedInContactsFromGroupPage(
        searchRequest,
        typeaheadQuery,
        metadata
      ) {
        const result = {
          target: backendTopics.apiProxy,
          command: "getLinkedInContactsFromGroupPage",
          args: {
            parameter: {
              searchRequest,
              typeaheadQuery,
            },
            metadata,
          },
        };
        return result;
      },
      createGetSalesNavigatorContactsFromSearchPage(
        searchRequest,
        referer,
        metadata
      ) {
        const result = {
          target: backendTopics.apiProxy,
          command: "getSalesNavigatorContactsFromSearchPage",
          args: {
            parameter: {
              searchRequest,
              referer,
            },
            metadata,
          },
        };
        return result;
      },
      createGetSalesNavigatorContactsFromLeadsList(
        searchRequest,
        referer,
        metadata
      ) {
        const result = {
          target: backendTopics.apiProxy,
          command: "getSalesNavigatorContactsFromLeadsList",
          args: {
            parameter: {
              searchRequest,
              referer,
            },
            metadata,
          },
        };
        return result;
      },
      createGetLinkedInConnectionsCount(metadata) {
        const result = {
          target: backendTopics.apiProxy,
          command: "getLinkedInConnectionsCount",
          args: {
            parameter: {},
            metadata,
          },
        };
        return result;
      },
      createGetLinkedInContactsFromMyConnections(start, count, metadata) {
        const result = {
          target: backendTopics.apiProxy,
          command: "getLinkedInContactsFromMyConnections",
          args: {
            parameter: {
              start,
              count,
            },
            metadata,
          },
        };
        return result;
      },
    },
  },
  automation: {
    createGetExportHref(exportId, metadata) {
      const result = {
        target: backendTopics.exportContacts,
        command: "getExportHref",
        args: {
          parameter: {
            exportId,
          },
          metadata,
        },
      };
      return result;
    },
  },
  reliability: {
    createReliablePublish(command, metadata) {
      const result = {
        target: backendTopics.reliability,
        command: "reliablePublish",
        args: {
          parameter: {
            command,
          },
          metadata,
        },
      };
      return result;
    },
  },
  logging: {
    createLogWaring(logEntryId, parameters, metadata) {
      const result = {
        target: backendTopics.logging,
        command: "logWarning",
        args: {
          parameter: {
            logEntryId,
            parameters,
          },
          metadata,
        },
        skipTracing: true,
      };
      return result;
    },
    createLogError(logEntryId, parameters, metadata) {
      const result = {
        target: backendTopics.logging,
        command: "logError",
        args: {
          parameter: {
            logEntryId,
            parameters,
          },
          metadata,
        },
        skipTracing: true,
      };
      return result;
    },
  },
  linkedInProxy: {
    headers: {
      createGetLastLinkedInRequestHeaders(metadata) {
        const result = {
          target: backendTopics.linkedInProxyHeaders,
          command: "getLastLinkedInRequestHeaders",
          args: {
            metadata,
          },
        };
        return result;
      },
      saveLinkedInHeaders(metadata) {
        const result = {
          target: backendTopics.linkedInProxyHeaders,
          command: "saveLinkedInHeaders",
          args: {
            metadata,
          },
        };
        return result;
      },
    },
    authorize: {
      V2: {
        createGetConnectionsCount(publicIdentifier, headers, metadata) {
          const result = {
            target: backendTopics.linkedInProxyV2,
            command: "getConnectionsCount",
            args: {
              parameter: {
                publicIdentifier,
                headers,
              },
              metadata,
            },
          };
          return result;
        },
      },
    },
  },
  localStorage: {
    createGetSnailyProfile(metadata) {
      const result = {
        target: backendTopics.localStorage,
        command: "getSnailyProfile",
        args: {
          metadata,
          checkSignedInLinkedInUser: true,
        },
      };
      return result;
    },
    createGetLinkedInProfile(metadata) {
      const result = {
        target: backendTopics.localStorage,
        command: "getLinkedInProfile",
        args: {
          metadata,
        },
      };
      return result;
    },
  },
  linkedInService: {
    createGetContactsFromSearchPage(parameters, start, referrer, metadata) {
      const result = {
        target: backendTopics.linkedInService,
        command: "getContactsFromSearchPage",
        args: {
          parameter: {
            parameters,
            start,
            referrer,
          },
          metadata,
        },
      };
      return result;
    },
    createGetContactsFromGroupPage(
      groupId,
      start,
      pageSize,
      typeaheadQuery,
      metadata
    ) {
      const result = {
        target: backendTopics.linkedInService,
        command: "getContactsFromGroupPage",
        args: {
          parameter: {
            groupId,
            start,
            pageSize,
            typeaheadQuery,
          },
          metadata,
        },
      };
      return result;
    },
    createGetContactsFromConnections(start, count, metadata) {
      const result = {
        target: backendTopics.linkedInService,
        command: "getContactsFromConnections",
        args: {
          parameter: {
            start,
            count,
          },
          metadata,
        },
      };
      return result;
    },
  },
  salesNavigatorService: {
    createGetContactsFromSearchPage(searchRequest, referer, metadata) {
      const result = {
        target: backendTopics.salesNavigatorService,
        command: "getContactsFromSearchPage",
        args: {
          parameter: {
            searchRequest,
            referer,
          },
          metadata,
        },
      };
      return result;
    },
    createGetContactsFromLeadsList(searchRequest, referer, metadata) {
      const result = {
        target: backendTopics.salesNavigatorService,
        command: "getContactsFromLeadsList",
        args: {
          parameter: {
            searchRequest,
            referer,
          },
          metadata,
        },
      };
      return result;
    },
  },
};
